import { useCallback, useEffect, useMemo, useState } from "react";
import { AuditoriaService } from "../services/auditoriaService";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { mapPaymentAuditItemsToRows, mapOrganizationGroupsToRows } from "../utils/auditoriaMapper";
import type { OrganizationAuditGroup } from "../types/auditoria-api.types";
import { AUDITORIA_PAGE_SIZE, PAGINATION_SIZES } from "../constants/auditoria.constants";
import type { AuditoriaRow, PaymentAuditFiltersState } from "../types/auditoria.types";

const getDefaultFilters = (): PaymentAuditFiltersState => ({
  organization_name: "",
  client_name: "",
  currency: "",
  amount_min: "",
  amount_max: "",
  start_date: "",
  end_date: "",
  payment_source: "",
});

function getPageNumbers(currentPage: number, lastPage: number): (number | string)[] {
  const maxPagesToShow = 7;
  if (lastPage <= maxPagesToShow) {
    return Array.from({ length: lastPage }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", lastPage];
  }
  if (currentPage >= lastPage - 2) {
    return [1, "...", lastPage - 3, lastPage - 2, lastPage - 1, lastPage];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", lastPage];
}

export interface UsePaymentAuditSectionReturn {
  filters: PaymentAuditFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<PaymentAuditFiltersState>>;
  page: number;
  pageSize: number;
  rows: AuditoriaRow[];
  totalItems: number;
  lastPage: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
  from: number;
  to: number;
  isLoading: boolean;
  error: string | null;
  paginationOptions: { label: string; value: string }[];
  pageNumbers: (number | string)[];
  handleApplyFilters: () => void;
  handleResetFilters: () => void;
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (value: string) => void;
  handleViewHistory: (entityId: string, entityType: string) => void;
  handleCloseHistoryModal: () => void;
  historyModalOpen: boolean;
  historyEntityId: string;
  historyEntityType: string;
  archivosGalleryOpen: boolean;
  selectedArchivosRow: AuditoriaRow | null;
  handleViewArchivos: (row: AuditoriaRow) => void;
  handleCloseArchivosModal: () => void;
  refetch: () => void;
  noAccess: boolean;
}

export function usePaymentAuditSection(): UsePaymentAuditSectionReturn {
  const [filters, setFilters] = useState<PaymentAuditFiltersState>(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<PaymentAuditFiltersState>(getDefaultFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(AUDITORIA_PAGE_SIZE);
  const [rows, setRows] = useState<AuditoriaRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noAccess, setNoAccess] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyEntityId, setHistoryEntityId] = useState("");
  const [historyEntityType, setHistoryEntityType] = useState("");
  const [archivosGalleryOpen, setArchivosGalleryOpen] = useState(false);
  const [selectedArchivosRow, setSelectedArchivosRow] = useState<AuditoriaRow | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setNoAccess(false);
    try {
      const skip = (page - 1) * pageSize;
      const params: Parameters<typeof AuditoriaService.fetchPaymentAudit>[0] = {
        skip,
        limit: pageSize,
        ...(appliedFilters.organization_name.trim() && {
          organization_name: appliedFilters.organization_name.trim(),
        }),
        ...(appliedFilters.client_name.trim() && {
          client_name: appliedFilters.client_name.trim(),
        }),
        ...(appliedFilters.currency.trim() && {
          currency: appliedFilters.currency.trim(),
        }),
        ...(appliedFilters.amount_min.trim() && {
          amount_min: appliedFilters.amount_min.trim(),
        }),
        ...(appliedFilters.amount_max.trim() && {
          amount_max: appliedFilters.amount_max.trim(),
        }),
        ...(appliedFilters.start_date.trim() && {
          start_date: `${appliedFilters.start_date.trim()}T00:00:00.000Z`,
        }),
        ...(appliedFilters.end_date.trim() && {
          end_date: `${appliedFilters.end_date.trim()}T23:59:59.999Z`,
        }),
        ...(appliedFilters.payment_source.trim() && {
          payment_source: appliedFilters.payment_source.trim(),
        }),
      };
      const response = await AuditoriaService.fetchPaymentAudit(params);
      const data = response.data;
      const isGrouped =
        data.data.length > 0 &&
        "logs" in (data.data[0] as OrganizationAuditGroup);
      if (isGrouped) {
        const groups = data.data as OrganizationAuditGroup[];
        setRows(mapOrganizationGroupsToRows(groups));
      } else {
        setRows(mapPaymentAuditItemsToRows(data.data as Parameters<typeof mapPaymentAuditItemsToRows>[0]));
      }
      setTotalItems(data.total);
    } catch (err) {
      if (HttpApiService.isAccessDeniedError(err)) {
        setNoAccess(true);
        setRows([]);
        setTotalItems(0);
        return;
      }
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "Error al cargar los registros de pagos";
      setError(message);
      setRows([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, page, pageSize]);

  useEffect(() => {
    if (!hasAppliedFilters) {
      setRows([]);
      setTotalItems(0);
      setError(null);
      setIsLoading(false);
      return;
    }
    fetchData();
  }, [hasAppliedFilters, fetchData]);

  const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));
  const canGoPrevious = page > 1;
  const canGoNext = page < lastPage;
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  const paginationOptions = useMemo(
    () => PAGINATION_SIZES.map((size) => ({ label: String(size), value: String(size) })),
    []
  );

  const pageNumbers = useMemo(() => getPageNumbers(page, lastPage), [page, lastPage]);

  const handleApplyFilters = useCallback(() => {
    setHasAppliedFilters(true);
    setAppliedFilters(filters);
    setPage(1);
  }, [filters]);

  const handleResetFilters = useCallback(() => {
    const empty = getDefaultFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
    setHasAppliedFilters(false);
    setRows([]);
    setTotalItems(0);
    setError(null);
    setIsLoading(false);
  }, []);

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);

  const handleItemsPerPageChange = useCallback((value: string) => {
    setPageSize(Number(value));
    setPage(1);
  }, []);

  const handleViewHistory = useCallback((entityId: string, entityType: string) => {
    setHistoryEntityId(entityId);
    setHistoryEntityType(entityType);
    setHistoryModalOpen(true);
  }, []);

  const handleCloseHistoryModal = useCallback(() => {
    setHistoryModalOpen(false);
    setHistoryEntityId("");
    setHistoryEntityType("");
  }, []);

  const handleViewArchivos = useCallback((row: AuditoriaRow) => {
    setSelectedArchivosRow(row);
    setArchivosGalleryOpen(true);
  }, []);

  const handleCloseArchivosModal = useCallback(() => {
    setArchivosGalleryOpen(false);
    setSelectedArchivosRow(null);
  }, []);

  const refetch = useCallback(() => {
    if (hasAppliedFilters) fetchData();
  }, [hasAppliedFilters, fetchData]);

  return {
    filters,
    setFilters,
    page,
    pageSize,
    rows,
    totalItems,
    lastPage,
    canGoPrevious,
    canGoNext,
    from,
    to,
    isLoading,
    error,
    paginationOptions,
    pageNumbers,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleItemsPerPageChange,
    handleViewHistory,
    handleCloseHistoryModal,
    historyModalOpen,
    historyEntityId,
    historyEntityType,
    archivosGalleryOpen,
    selectedArchivosRow,
    handleViewArchivos,
    handleCloseArchivosModal,
    refetch,
    noAccess,
  };
}
